import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertTriangle, Bell, CalendarClock, CheckCircle2, Loader2, Lock,
  RefreshCw, ShieldCheck, UserX,
} from 'lucide-react';
import { adminApi, clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { bizDate } from '@/lib/dates';
import { toast } from 'sonner';

const P_TONE = {
  pending: 'bg-amber-100 text-amber-700',
  opened: 'bg-sky-100 text-sky-700',
  completed: 'bg-emerald-100 text-emerald-700',
};

function ConsoleKpi({ label, value, color, testid }) {
  return (
    <div className="rounded-lg border bg-muted/20 px-3 py-2 text-center min-w-0" data-testid={testid}>
      <p className="text-base font-bold tabular-nums leading-tight break-words" style={color ? { color } : undefined}>{value}</p>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );
}

// Operational campaign console: participation tracking ONLY (never answers).
// mode='admin' → HM Advisor (remind + extend deadline + close);
// mode='client' → Client Admin (remind only).
export const ClimaraCampaignConsole = ({ campaignId, mode = 'admin', onChanged, onOpenReport }) => {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const navigate = useNavigate();
  const apiSet = mode === 'admin' ? adminApi : clientApi;
  // Item 4 (UAT iter 2) — Client Admins jump straight to the collaborator record.
  const openCollaborator = (p) => {
    if (mode !== 'client') return;
    navigate(`/client/employees?q=${encodeURIComponent(p.email || p.name || '')}`);
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [newDeadline, setNewDeadline] = useState('');
  const [confirmClose, setConfirmClose] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await apiSet.climaraCampaignConsole(campaignId));
      setRefreshedAt(new Date());
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const remind = async (userIds, key) => {
    setBusy(key);
    try {
      const res = await apiSet.climaraRemindCampaign(campaignId, userIds);
      if (!res.reminded) {
        toast.info(isES
          ? 'No había participantes pendientes por recordar.'
          : 'There were no pending participants to remind.');
      } else if (res.email_sent === false) {
        toast.success(isES
          ? `Recordatorio registrado para ${res.reminded} colaborador(es). Lo verán como asignación pendiente al iniciar sesión — no se envió ningún correo (correo no configurado).`
          : `Reminder recorded for ${res.reminded} employee(s). They will see it as a pending assignment at login — no email was sent (email not configured).`);
      } else {
        toast.success(isES
          ? `Recordatorio enviado a ${res.reminded} colaborador(es)`
          : `Reminder sent to ${res.reminded} employee(s)`);
      }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  const extend = async () => {
    if (!newDeadline) { toast.error(isES ? 'Selecciona la nueva fecha.' : 'Pick the new date.'); return; }
    setBusy('extend');
    try {
      await adminApi.climaraExtendDeadline(campaignId, newDeadline);
      toast.success(isES ? 'Fecha límite actualizada' : 'Deadline updated');
      setNewDeadline('');
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  const close = async () => {
    setBusy('close');
    try {
      await (mode === 'admin' ? adminApi : clientApi).climaraCloseCampaign(campaignId);
      toast.success(isES
        ? 'Campaña cerrada — la recolección se detuvo. Siguiente paso: generar el reporte ejecutivo.'
        : 'Campaign closed — collection stopped. Next step: generate the executive report.');
      setConfirmClose(false);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  if (loading) {
    return (
      <div className="flex items-center text-sm text-muted-foreground py-6" data-testid="climara-console-loading">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />{isES ? 'Cargando consola…' : 'Loading console…'}
      </div>
    );
  }
  if (!data) return null;
  const { campaign, kpis, participants, phase, report, self_service: selfService = true, events = [], latest_event: latestEvent } = data;
  const isActive = campaign.status === 'active';
  const participationComplete = !!(kpis.invited && kpis.completed >= kpis.invited);
  const pLabel = (p) => ({
    pending: isES ? 'Pendiente' : 'Pending',
    opened: isES ? 'En curso' : 'In progress',
    completed: isES ? 'Completada' : 'Completed',
  }[p]);

  // Founder UX sprint — campaign state machine + dynamic "Próximo paso".
  const isClosed = campaign.status === 'closed';
  const PHASES = [
    'preparing', 'invitations_sent', 'collecting',
    ...(isClosed ? ['campaign_closed'] : []),
    'ready_to_consolidate', 'report_review', 'report_published',
  ];
  const PHASE_LABELS = {
    preparing: isES ? 'Campaña creada' : 'Campaign created',
    invitations_sent: isES ? 'Invitaciones enviadas' : 'Invitations sent',
    collecting: isES ? 'Recopilando respuestas' : 'Collecting responses',
    campaign_closed: isES ? 'Campaña cerrada' : 'Campaign closed',
    ready_to_consolidate: isES ? 'Reporte ejecutivo por generar' : 'Executive report to generate',
    report_review: isES ? 'Reporte en revisión' : 'Report under review',
    report_published: isES ? 'Publicado — disponible para el cliente' : 'Published — available to client',
  };
  const phaseIdx = Math.max(PHASES.indexOf(phase), 0);
  const NEXT_STEP = {
    preparing: mode === 'admin'
      ? (isES ? 'Asigna participantes para iniciar la campaña.' : 'Assign participants to start the campaign.')
      : (selfService
          ? (isES ? 'La campaña aún no tiene participantes asignados. Asigna participantes y lanza las invitaciones desde “Lanzar campaña Climara” para iniciar la recolección de respuestas.' : 'The campaign has no participants assigned yet. Assign participants and launch the invitations from “Launch Climara campaign” to start collecting responses.')
          : (isES ? 'Tu asesor de CTH asignará participantes para iniciar la campaña.' : 'Your CTH advisor will assign participants to start the campaign.')),
    invitations_sent: isES
      ? 'Invitaciones enviadas. Espera las respuestas o envía recordatorios a los pendientes.'
      : 'Invitations sent. Wait for responses or send reminders to pending participants.',
    collecting: mode === 'admin'
      ? (isES
          ? `${kpis.completed} de ${kpis.invited} participantes han completado (${kpis.rate_pct}%). Puedes seguir recopilando respuestas o generar el reporte ejecutivo cuando consideres suficiente la participación — la decisión es del consultor, no hay umbral mínimo. Cerrar la campaña es OPCIONAL: detiene la recolección, pero puedes generar el reporte sin cerrarla.`
          : `${kpis.completed} of ${kpis.invited} participants completed (${kpis.rate_pct}%). You may keep collecting responses or generate the executive report when you consider participation sufficient — the consultant decides, there is no minimum threshold. Closing the campaign is OPTIONAL: it stops collection, but you can generate the report without closing.`)
      : (isES
          ? `${kpis.completed} de ${kpis.invited} participantes han completado (${kpis.rate_pct}%). La campaña sigue recopilando respuestas; Corporate Talent Hub generará el reporte ejecutivo cuando la participación sea suficiente.`
          : `${kpis.completed} of ${kpis.invited} participants completed (${kpis.rate_pct}%). The campaign is still collecting responses; Corporate Talent Hub will generate the executive report once participation is sufficient.`),
    ready_to_consolidate: mode === 'admin'
      ? (isES ? 'Participación lista. Genera el reporte ejecutivo de clima.' : 'Participation ready. Generate the executive climate report.')
      : (isES ? 'Participación completa. Corporate Talent Hub generará el reporte ejecutivo.' : 'Participation complete. Corporate Talent Hub will generate the executive report.'),
    report_review: mode === 'admin'
      ? (isES ? 'Reporte generado. Revísalo y publícalo para el cliente.' : 'Report generated. Review it and release it to the client.')
      : (isES ? 'El reporte ejecutivo está en revisión por Corporate Talent Hub. Se publicará en tu panel Climara.' : 'The executive report is under Corporate Talent Hub review. It will be published to your Climara panel.'),
    report_published: mode === 'admin'
      ? (isES ? 'Reporte ejecutivo publicado — ya está disponible para el cliente. Campaña completada.' : 'Executive report published — now available to the client. Campaign completed.')
      : (isES ? 'El reporte ejecutivo está disponible en tu panel Climara.' : 'The executive report is available in your Climara panel.'),
  }[phase] || '';

  // Finding 4 — who performs the next action, per phase.
  const RESPONSIBLE = {
    preparing: mode === 'client' && selfService
      ? (isES ? 'Administrador del cliente' : 'Client Admin')
      : (isES ? 'Asesor CTH' : 'CTH Advisor'),
    invitations_sent: isES ? 'Participantes' : 'Participants',
    collecting: isES ? 'Participantes · decisión del Asesor CTH' : 'Participants · CTH Advisor decision',
    ready_to_consolidate: isES ? 'Asesor CTH' : 'CTH Advisor',
    report_review: isES ? 'Corporate Talent Hub (revisión y publicación)' : 'Corporate Talent Hub (review & release)',
    report_published: isES ? 'Cliente (lectura del reporte)' : 'Client (reads the report)',
  }[phase] || '';

  const relDay = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const diff = Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
    if (diff <= 0) return isES ? 'Hoy' : 'Today';
    if (diff === 1) return isES ? 'Ayer' : 'Yesterday';
    return d.toLocaleDateString(isES ? 'es-ES' : 'en-US');
  };
  const feedCompleted = participants
    .filter((p) => p.participation === 'completed')
    .sort((x, y) => String(y.completed_at || '').localeCompare(String(x.completed_at || '')));
  const feedOpened = participants.filter((p) => p.participation === 'opened');
  const feedPending = participants.filter((p) => p.participation === 'pending');

  return (
    <div className="space-y-4" data-testid="climara-campaign-console">
      {/* Req 1 — campaign identity + status header: the console is the operational center */}
      <div className="flex flex-wrap items-center gap-2" data-testid="climara-console-header">
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate" data-testid="climara-console-campaign-name">
            {campaign.name}{campaign.wave_label ? ` · ${campaign.wave_label}` : ''}
          </p>
          <p className="text-xs text-muted-foreground">
            {isES ? 'Creada' : 'Created'} {campaign.created_at ? bizDate(campaign.created_at, isES ? 'es' : 'en') : '—'}
            {campaign.closed_at ? ` · ${isES ? 'Cerrada' : 'Closed'} ${bizDate(campaign.closed_at, isES ? 'es' : 'en')}` : ''}
          </p>
        </div>
        <Badge
          className={!isActive ? 'bg-slate-200 text-slate-700'
            : phase === 'preparing' ? 'bg-amber-100 text-amber-700'
            : participationComplete ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700'}
          data-testid="climara-console-status-badge">
          {!isActive ? (isES ? 'Cerrada — recolección detenida' : 'Closed — collection stopped')
            : phase === 'preparing'
              ? (isES ? 'Creada — participantes pendientes' : 'Created — participants pending')
              : participationComplete
                ? (isES ? 'Participación completa — lista para revisión/cierre' : 'Participation complete — ready for review/closure')
                : (isES ? 'Activa — recopilando respuestas' : 'Active — collecting responses')}
        </Badge>
      </div>

      {/* FIX 30 — latest meaningful activity, highlighted while unreviewed */}
      {latestEvent && (
        <LatestActivityStrip event={latestEvent} campaignId={campaignId} isES={isES} />
      )}

      {/* Founder UX sprint — campaign state machine */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="climara-phase-stepper">
        {PHASES.map((p, i) => (
          <React.Fragment key={p}>
            <div className={`flex items-center gap-1 shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium
                             ${i < phaseIdx ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                               : i === phaseIdx ? 'bg-primary/10 border-primary/40 text-primary'
                               : 'bg-muted/30 border-muted text-muted-foreground'}`}
                 data-testid={`climara-phase-${p}${i === phaseIdx ? '-current' : ''}`}>
              {i < phaseIdx && <CheckCircle2 className="h-3 w-3" />}
              {PHASE_LABELS[p]}
            </div>
            {i < PHASES.length - 1 && <div className={`h-px w-3 shrink-0 ${i < phaseIdx ? 'bg-emerald-300' : 'bg-border'}`} />}
          </React.Fragment>
        ))}
        <span className="flex-1" />
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-muted-foreground shrink-0"
                onClick={load} data-testid="climara-console-refresh-btn">
          <RefreshCw className="h-3 w-3 mr-1" />
          {refreshedAt
            ? (isES ? `Actualizado ${refreshedAt.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}` : `Updated ${refreshedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`)
            : (isES ? 'Actualizar' : 'Refresh')}
        </Button>
      </div>

      {/* Dynamic next step — the platform always answers "what should I do next?" */}
      <div className="flex items-start gap-2.5 rounded-lg border border-primary/25 bg-primary/5 p-3" data-testid="climara-next-step">
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wider font-semibold text-primary">
            {isES ? 'Próximo paso' : 'Next step'}
          </p>
          <p className="text-xs leading-relaxed mt-0.5">{NEXT_STEP}</p>
          {RESPONSIBLE && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="climara-next-step-responsible">
              {isES ? 'Responsable del siguiente paso: ' : 'Responsible for the next step: '}
              <span className="font-semibold text-foreground">{RESPONSIBLE}</span>
            </p>
          )}
          {report && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="climara-next-step-report-status">
              {isES ? 'Estado del reporte: ' : 'Report status: '}
              <span className="font-semibold text-foreground">
                {report.status === 'released'
                  ? (isES ? 'Publicado' : 'Released')
                  : (isES ? 'Generado — pendiente de publicación' : 'Generated — pending release')}
              </span>
              {report.generated_at ? ` · ${bizDate(report.generated_at, isES ? 'es' : 'en')}` : ''}
            </p>
          )}
        </div>
        {mode === 'admin' && onOpenReport && ['collecting', 'ready_to_consolidate', 'report_review'].includes(phase) && (
          <Button size="sm" onClick={onOpenReport} className="shrink-0" data-testid="climara-next-step-report-btn">
            {phase === 'report_review'
              ? (isES ? 'Revisar reporte' : 'Review report')
              : (isES ? 'Generar reporte' : 'Generate report')}
          </Button>
        )}
      </div>

      {/* Req 4 — available operational options once responses start arriving */}
      {mode === 'admin' && isActive && kpis.completed >= 1 && (!report || report.status !== 'released') && (
        <div className="rounded-lg border p-3" data-testid="climara-console-options">
          <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
            {isES ? 'Opciones disponibles' : 'Available options'}
          </p>
          <ul className="text-xs leading-relaxed text-muted-foreground space-y-0.5 list-disc pl-4">
            <li>{isES ? 'Continuar recopilando respuestas (la campaña sigue abierta).' : 'Keep collecting responses (the campaign remains open).'}</li>
            <li>{isES ? 'Enviar recordatorios a los participantes pendientes.' : 'Send reminders to pending participants.'}</li>
            <li>{isES ? 'Cerrar la campaña si lo deseas — es OPCIONAL y detiene la recolección.' : 'Close the campaign if desired — it is OPTIONAL and stops collection.'}</li>
            <li>
              <span className="font-medium text-foreground">
                {isES ? 'Generar el reporte ejecutivo' : 'Generate the executive report'}
              </span>
              {isES
                ? ' — no requiere cerrar la campaña; el consultor decide cuándo la participación es suficiente.'
                : ' — closing the campaign is not required; the consultant decides when participation is sufficient.'}
            </li>
          </ul>
        </div>
      )}

      {/* Anonymity model — operational tracking vs confidential answers */}
      <div className="flex items-start gap-2.5 rounded-lg border border-emerald-200/60 bg-emerald-50/40 p-3" data-testid="climara-console-anon-note">
        <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          {isES
            ? 'Seguimiento operativo de participación: muestra quién respondió, nunca qué respondió. Las respuestas individuales son estrictamente confidenciales; todos los resultados, análisis y reportes son 100% anónimos y agregados.'
            : 'Operational participation tracking: it shows who responded, never what they answered. Individual answers are strictly confidential; all results, analytics and reports are 100% anonymous and aggregated.'}
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        <ConsoleKpi label={isES ? 'Invitaciones' : 'Invitations'} value={kpis.invited} testid="climara-console-kpi-invited" />
        <ConsoleKpi label={isES ? 'Respondidas' : 'Completed'} value={kpis.completed} color="#16a34a" testid="climara-console-kpi-completed" />
        <ConsoleKpi label={isES ? 'En curso' : 'In progress'} value={kpis.opened} color="#0284c7" testid="climara-console-kpi-opened" />
        <ConsoleKpi label={isES ? 'Pendientes' : 'Pending'} value={kpis.pending} color={kpis.pending ? '#d97706' : undefined} testid="climara-console-kpi-pending" />
        <ConsoleKpi label={isES ? 'Participación' : 'Rate'} value={`${kpis.rate_pct}%`} color={kpis.rate_pct >= 70 ? '#16a34a' : '#d97706'} testid="climara-console-kpi-rate" />
        <ConsoleKpi
          label={isES ? 'Fecha límite' : 'Deadline'}
          value={kpis.deadline ? bizDate(kpis.deadline, isES ? 'es' : 'en') : '—'}
          color={kpis.days_left != null && kpis.days_left < 0 ? '#dc2626' : undefined}
          testid="climara-console-kpi-deadline"
        />
      </div>
      {(kpis.days_left != null || kpis.not_activated_count > 0) && (
        <div className="flex flex-wrap gap-2 text-xs">
          {kpis.days_left != null && (
            <Badge variant="outline" className="gap-1 font-normal" data-testid="climara-console-days-left">
              <CalendarClock className="h-3 w-3" />
              {kpis.days_left >= 0
                ? (isES ? `${kpis.days_left} días restantes` : `${kpis.days_left} days left`)
                : (isES ? `Vencida hace ${Math.abs(kpis.days_left)} días` : `Overdue by ${Math.abs(kpis.days_left)} days`)}
            </Badge>
          )}
          {kpis.not_activated_count > 0 && (
            <Badge variant="outline" className="gap-1 font-normal text-amber-700 border-amber-300" data-testid="climara-console-not-activated">
              <UserX className="h-3 w-3" />
              {isES
                ? `${kpis.not_activated_count} cuenta(s) sin activar — el colaborador aún no ha iniciado sesión`
                : `${kpis.not_activated_count} account(s) not activated — the employee has never logged in`}
            </Badge>
          )}
        </div>
      )}

      {/* Actions */}
      {isActive && (
        <div className="flex flex-wrap items-end gap-2">
          <Button size="sm" variant="outline" disabled={busy === 'remind-all' || kpis.pending + kpis.opened === 0}
                  onClick={() => remind([], 'remind-all')} data-testid="climara-console-remind-all">
            {busy === 'remind-all' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Bell className="h-3.5 w-3.5 mr-1" />}
            {isES ? 'Recordar a todos los pendientes' : 'Remind all pending'}
          </Button>
          {mode === 'admin' && (
            <div className="flex items-end gap-1.5">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">
                  {isES ? 'Nueva fecha límite' : 'New deadline'}
                </label>
                <Input type="date" value={newDeadline} onChange={(e) => setNewDeadline(e.target.value)}
                       className="h-8 w-40 mt-0.5 text-xs" data-testid="climara-console-deadline-input" />
              </div>
              <Button size="sm" variant="outline" onClick={extend} disabled={busy === 'extend'}
                      data-testid="climara-console-extend-btn">
                {busy === 'extend' && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                {isES ? 'Extender' : 'Extend'}
              </Button>
            </div>
          )}
          {(mode === 'admin' || selfService) && (
            <Button size="sm" variant="outline" onClick={() => setConfirmClose(true)} disabled={busy === 'close'}
                    className="ml-auto" data-testid="climara-console-close-btn">
              <Lock className="h-3.5 w-3.5 mr-1" />{isES ? 'Cerrar campaña' : 'Close campaign'}
            </Button>
          )}
        </div>
      )}

      {/* Finding 3 — explicit, explained closure confirmation */}
      {confirmClose && isActive && (
        <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3 space-y-2" data-testid="climara-close-confirm">
          <p className="text-xs leading-relaxed text-amber-900">
            {isES
              ? `¿Cerrar la campaña? Se detendrá la recolección de respuestas (participación actual: ${kpis.completed} de ${kpis.invited}, ${kpis.rate_pct}%). Cerrar es OPCIONAL: también puedes generar el reporte ejecutivo sin cerrar y seguir recibiendo respuestas. Después de cerrar, el siguiente paso es generar el reporte.`
              : `Close the campaign? Response collection will stop (current participation: ${kpis.completed} of ${kpis.invited}, ${kpis.rate_pct}%). Closing is OPTIONAL: you can also generate the executive report without closing and keep receiving responses. After closing, the next step is generating the report.`}
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="destructive" onClick={close} disabled={busy === 'close'}
                    data-testid="climara-close-confirm-btn">
              {busy === 'close' && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              {isES ? 'Sí, cerrar campaña' : 'Yes, close campaign'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmClose(false)}
                    data-testid="climara-close-cancel-btn">
              {isES ? 'Cancelar' : 'Cancel'}
            </Button>
          </div>
        </div>
      )}

      {/* Founder UX sprint — latest response activity (participation only, never answers) */}
      <div className="rounded-lg border p-3" data-testid="climara-activity-feed">
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
          {isES ? 'Actividad reciente' : 'Latest responses'}
        </p>
        <div className="space-y-1.5 text-xs">
          {feedCompleted.slice(0, 6).map((p) => (
            <div key={p.assignment_id} className="flex items-center gap-2" data-testid={`climara-feed-completed-${p.user_id}`}>
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
              <span className="font-medium">{p.name}</span>
              <span className="text-muted-foreground">{isES ? 'Completada' : 'Completed'} · {relDay(p.completed_at)}</span>
            </div>
          ))}
          {feedOpened.map((p) => (
            <div key={p.assignment_id} className="flex items-center gap-2" data-testid={`climara-feed-opened-${p.user_id}`}>
              <Loader2 className="h-3.5 w-3.5 text-sky-600 shrink-0" />
              <span className="font-medium">{p.name}</span>
              <span className="text-muted-foreground">{isES ? 'En curso' : 'In progress'}</span>
            </div>
          ))}
          {feedPending.map((p) => (
            <div key={p.assignment_id} className="flex items-center gap-2" data-testid={`climara-feed-pending-${p.user_id}`}>
              <UserX className="h-3.5 w-3.5 text-amber-600 shrink-0" />
              <span className="font-medium">{p.name}</span>
              <span className="text-muted-foreground">
                {p.account_activated
                  ? (isES ? 'Invitación pendiente' : 'Invitation pending')
                  : (isES ? 'Invitación no abierta — cuenta sin activar' : 'Invitation not opened — account not activated')}
              </span>
            </div>
          ))}
          {participants.length === 0 && (
            <p className="text-muted-foreground italic">{isES ? 'Sin actividad todavía.' : 'No activity yet.'}</p>
          )}
        </div>
        {/* Finding 6 — reminder history recorded on the campaign timeline */}
        {events.filter((e) => e.type === 'reminder_sent').length > 0 && (
          <div className="mt-3 pt-2 border-t" data-testid="climara-reminder-history">
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
              {isES ? 'Historial de recordatorios' : 'Reminder history'}
            </p>
            <div className="space-y-1 text-xs">
              {events.filter((e) => e.type === 'reminder_sent').slice(0, 5).map((e, i) => (
                <div key={i} className="flex items-center gap-2 text-muted-foreground" data-testid={`climara-reminder-event-${i}`}>
                  <Bell className="h-3 w-3 text-amber-600 shrink-0" />
                  <span>
                    {isES
                      ? `Recordatorio enviado a ${e.targets} participante(s)`
                      : `Reminder sent to ${e.targets} participant(s)`}
                    {' · '}{relDay(e.at)}{e.by ? ` · ${isES ? 'por' : 'by'} ${e.by}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Participants */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">{isES ? 'Colaborador' : 'Employee'}</th>
              <th className="text-left px-3 py-2">{isES ? 'Departamento' : 'Department'}</th>
              <th className="text-left px-3 py-2">{isES ? 'Cuenta' : 'Account'}</th>
              <th className="text-left px-3 py-2">{isES ? 'Participación' : 'Participation'}</th>
              <th className="text-left px-3 py-2">{isES ? 'Última actividad' : 'Last activity'}</th>
              <th className="text-left px-3 py-2">{isES ? 'Último recordatorio' : 'Last reminder'}</th>
              {isActive && <th className="text-right px-3 py-2">{isES ? 'Acción' : 'Action'}</th>}
            </tr>
          </thead>
          <tbody className="divide-y">
            {participants.map((p) => (
              <tr key={p.assignment_id} data-testid={`climara-console-row-${p.user_id}`}>
                <td className="px-3 py-2">
                  {mode === 'client' ? (
                    <button type="button" onClick={() => openCollaborator(p)} className="text-left group"
                            title={isES ? 'Abrir en Colaboradores' : 'Open in Collaborators'}
                            data-testid={`climara-console-open-collab-${p.user_id}`}>
                      <p className="font-medium text-primary group-hover:underline">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.email}</p>
                    </button>
                  ) : (
                    <>
                      <p className="font-medium">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.email}</p>
                    </>
                  )}
                </td>
                <td className="px-3 py-2 text-muted-foreground">{p.department || '—'}</td>
                <td className="px-3 py-2">
                  {p.account_activated ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600">
                      <CheckCircle2 className="h-3 w-3" />{isES ? 'Activada' : 'Active'}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-600" title={isES ? 'Nunca ha iniciado sesión' : 'Has never logged in'}>
                      <UserX className="h-3 w-3" />{isES ? 'Sin activar' : 'Not activated'}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className="inline-flex items-center gap-1.5">
                    <Badge className={`text-xs ${P_TONE[p.participation]}`}>{pLabel(p.participation)}</Badge>
                    {p.overdue && (
                      <Badge variant="outline" className="text-xs text-rose-600 border-rose-300 gap-0.5">
                        <AlertTriangle className="h-2.5 w-2.5" />{isES ? 'Vencida' : 'Overdue'}
                      </Badge>
                    )}
                  </span>
                </td>
                <td className="px-3 py-2 text-muted-foreground" data-testid={`climara-console-last-activity-${p.user_id}`}>
                  {p.participation === 'completed'
                    ? `${isES ? 'Completó' : 'Completed'} · ${relDay(p.completed_at)}`
                    : p.participation === 'opened'
                      ? `${isES ? 'Avance' : 'Progress'} ${p.progress || 0}% · ${relDay(p.last_activity_at)}`
                      : (isES ? 'Sin actividad' : 'No activity')}
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {p.last_reminder_at
                    ? `${p.last_reminder_at.slice(0, 10)}${p.reminder_count > 1 ? ` (×${p.reminder_count})` : ''}`
                    : '—'}
                </td>
                {isActive && (
                  <td className="px-3 py-2 text-right">
                    {p.participation !== 'completed' && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs"
                              disabled={busy === p.user_id}
                              onClick={() => remind([p.user_id], p.user_id)}
                              data-testid={`climara-console-remind-${p.user_id}`}>
                        {busy === p.user_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bell className="h-3 w-3 mr-1" />}
                        {isES ? 'Recordar' : 'Remind'}
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {participants.length === 0 && (
              <tr><td colSpan={isActive ? 7 : 6} className="px-3 py-6 text-center text-muted-foreground">
                {isES ? 'Sin participantes.' : 'No participants.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClimaraCampaignConsole;

// A9 (limited Phase 2) — human labels for raw campaign event types that ship
// without label_es/label_en (e.g. reminder events persisted as {type, at, by}).
const EVENT_TYPE_LABELS = {
  reminder_sent: { es: 'Recordatorio enviado', en: 'Reminder sent' },
  launched: { es: 'Campaña lanzada', en: 'Campaign launched' },
  campaign_launched: { es: 'Campaña lanzada', en: 'Campaign launched' },
  closed: { es: 'Campaña cerrada', en: 'Campaign closed' },
  campaign_closed: { es: 'Campaña cerrada', en: 'Campaign closed' },
  deadline_extended: { es: 'Fecha límite extendida', en: 'Deadline extended' },
  report_released: { es: 'Informe publicado', en: 'Report released' },
};

// FIX 30 — "Última actividad" with an unread highlight that downgrades once
// the user has seen it (per-user, per-campaign localStorage marker).
function LatestActivityStrip({ event, campaignId, isES }) {
  const seenKey = `climara_seen_${campaignId}`;
  const [wasSeen] = useState(() => {
    try { return (localStorage.getItem(seenKey) || '') >= (event.at || ''); } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem(seenKey, event.at || new Date().toISOString()); } catch { /* noop */ }
  }, [seenKey, event.at]);
  const label = (isES ? event.label_es : event.label_en) || event.label_es
    || EVENT_TYPE_LABELS[event.type]?.[isES ? 'es' : 'en']
    || (event.type || '').replace(/_/g, ' ');
  return (
    <div
      className={`flex items-center gap-2.5 rounded-lg border p-2.5 ${wasSeen ? 'bg-muted/20' : 'bg-amber-50/70 border-amber-300'}`}
      data-testid="climara-latest-activity"
    >
      <Bell className={`h-4 w-4 shrink-0 ${wasSeen ? 'text-muted-foreground' : 'text-amber-600'}`} />
      <p className="text-xs leading-snug flex-1">
        <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mr-2">
          {isES ? 'Última actividad' : 'Latest activity'}
        </span>
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground"> · {bizDate(event.at, isES ? 'es' : 'en')}</span>
      </p>
      {!wasSeen && (
        <Badge className="bg-amber-100 text-amber-700 text-xs shrink-0" data-testid="climara-latest-activity-new">
          {isES ? 'NUEVO' : 'NEW'}
        </Badge>
      )}
    </div>
  );
}
